import { Button } from "@/components/ui/button";
import { Patient } from "@/services/patientService";
import { CheckCircle, Download, GitMerge, Loader2, X } from "lucide-react";
import { useMemo } from "react";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanversePatientUpsert } from "@/data/constantStrings";

export default function PatientsBulkActionsBar({
  isDarkMode,
  count,
  onMerge,
  onClear,
  selectedPatients,
  patients,
  onImport,
  isImporting,
}: {
  isDarkMode: boolean;
  count: number;
  selectedPatients: Set<string>;
  patients: Patient[];
  onMerge: () => void;
  onClear: () => void;
  onImport?: () => void;
  isImporting?: boolean;
}) {
  const titanCount = useMemo(() => {
    let count = 0;
    selectedPatients.forEach((patientId) => {
      const patient = patients.find((p) => p.id === patientId);
      if (patient && patient.imported === false) {
        count++;
      }
    });
    return count;
  }, [selectedPatients, patients]);
  const canAddPatient = checkUserPermissions(accessTitanversePatientUpsert);
  const canMerge = useMemo(() => {
    // Disable merge if count is not exactly 2
    if (count !== 2) return false;
    // Disable merge if any selected patient is a TITAN patient (imported === false)
    for (const patientId of selectedPatients) {
      const patient = patients.find((p) => p.id === patientId);
      if (patient && patient.imported === false) {
        return false;
      }
    }
    return true;
  }, [selectedPatients, patients, count]);

  if (count === 0) return null;

  const tooManyImported = selectedPatients.size > 5;
  return (
    <div className="fixed z-50 transform -translate-x-1/2 bottom-6 left-1/2">
      <div
        className={`flex items-center gap-4 px-6 py-4 rounded-lg shadow-lg border ${
          isDarkMode
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          <span className="font-medium">
            {count} patient{count !== 1 ? "s" : ""} selected
          </span>
        </div>

        <div className="w-px h-6 bg-gray-300" />

        <div className="flex items-center gap-2">
          {titanCount > 0 && (
            <Button
              size="sm"
              onClick={() => {
                if (!canAddPatient) {
                  setShowPermissionMessage(
                    "You don't have permission to import patients.",
                  );
                  setShowPermissionModal(true);
                  return;
                }
                onImport();
              }}
              disabled={tooManyImported || isImporting || !canAddPatient}
              className={`text-white ${
                isImporting
                  ? "bg-green-500 cursor-wait"
                  : !canAddPatient
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {isImporting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              Import ({titanCount})
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              if (!canAddPatient) {
                setShowPermissionMessage(
                  "You don't have permission to merge patients.",
                );
                setShowPermissionModal(true);
                return;
              }

              onMerge();
            }}
            disabled={!canMerge}
            title={
              !canMerge
                ? count !== 2
                  ? "Select exactly 2 patients to merge"
                  : "Cannot merge TITAN or imported patients"
                : undefined
            }
            className={`text-white ${
              !canMerge
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } ${!canAddPatient ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {!canMerge ? (
              <X className="w-4 h-4 mr-1" />
            ) : (
              <GitMerge className="w-4 h-4 mr-1" />
            )}
            Merge
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            className={`${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            Clear Selection
          </Button>
        </div>
      </div>
    </div>
  );
}
